import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { blogCategories } from "@/mocks/blogPosts";
import { useBlogPosts } from "@/hooks/useBlogPosts";
import { motion } from "motion/react";

const pageVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const { posts: allPosts, loading } = useBlogPosts();

  const featuredPosts = useMemo(() => allPosts.filter((p) => p.featured), [allPosts]);

  const filteredPosts = useMemo(() => {
    let posts = allPosts;
    if (activeCategory !== "all") {
      posts = posts.filter((p) => p.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      posts = posts.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          p.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    return posts;
  }, [allPosts, activeCategory, searchQuery]);

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
      <Navbar />
      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-[1280px] mx-auto">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="mb-8"
          >
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-article-line text-[#C4956A]"></i>
              <span className="text-xs font-medium text-[#C4956A]">블로그</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1C1917] mb-2">
              블로그
            </h1>
            <p className="text-sm text-[#8C8780]">
              경매 투자 노하우부터 부동산 시장 분석까지, 강스터디 전문가들의 인사이트를 만나보세요
            </p>
          </motion.div>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="mb-8"
          >
            <form onSubmit={handleSearchSubmit} className="relative max-w-[480px]">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-lg text-[#8C8780]"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="블로그 글 검색"
                className="w-full pl-12 pr-4 py-3 text-sm border border-[#E8E5E0] rounded-xl bg-white focus:outline-none focus:border-[#C4956A] focus:ring-2 focus:ring-[#C4956A]/10 transition-all"
              />
            </form>
          </motion.div>

          {/* Category Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="flex items-center gap-1.5 flex-wrap mb-8"
          >
            {blogCategories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-full transition-all whitespace-nowrap ${
                  activeCategory === cat.key
                    ? "bg-[#1C1917] text-white font-medium"
                    : "text-[#5C5650] bg-white border border-[#E8E5E0] hover:text-[#C4956A] hover:border-[#C4956A]/30"
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.label}
              </button>
            ))}
          </motion.div>

          {/* Featured Posts (only show when no filter/search active) */}
          {activeCategory === "all" && !searchQuery && (
            <motion.section
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="mb-12"
            >
              <div className="flex items-center gap-2 mb-5">
                <i className="ri-star-line text-[#C4956A]"></i>
                <h2 className="text-base font-bold text-[#1C1917]">주목할 만한 글</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {featuredPosts.slice(0, 3).map((post, idx) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 + idx * 0.1, duration: 0.45 }}
                    className="bg-white rounded-2xl border border-[#E8E5E0] overflow-hidden group cursor-pointer hover:border-[#C4956A]/30 transition-all"
                  >
                    <Link to={`/blog/${post.slug}`} className="block">
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img loading="lazy" decoding="async"
                          src={post.thumbnail}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="px-2.5 py-1 text-xs font-medium bg-[#C4956A] text-white rounded-full">
                            {post.categoryLabel}
                          </span>
                        </div>
                      </div>
                      <div className="p-5">
                        <h3 className="text-sm font-bold text-[#1C1917] mb-2 line-clamp-2 group-hover:text-[#C4956A] transition-colors leading-snug">
                          {post.title}
                        </h3>
                        <p className="text-xs text-[#8C8780] line-clamp-2 mb-3 leading-relaxed">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-[#B8B3AE]">
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full overflow-hidden border border-[#E8E5E0]">
                              <img loading="lazy" decoding="async" src={post.authorAvatar} alt={post.author} className="w-full h-full object-cover" />
                            </div>
                            <span>{post.author}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span>{post.createdAt}</span>
                            <span>·</span>
                            <span>{post.readTime}분</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.section>
          )}

          {/* All Posts Grid */}
          <motion.section
            variants={pageVariants}
            initial="hidden"
            animate="visible"
            key={activeCategory + searchQuery}
          >
            {filteredPosts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredPosts.map((post, idx) => (
                  <motion.div
                    key={post.id}
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    transition={{ delay: idx * 0.06 }}
                    className="bg-white rounded-2xl border border-[#E8E5E0] overflow-hidden group cursor-pointer hover:border-[#C4956A]/30 transition-all"
                  >
                    <Link to={`/blog/${post.slug}`} className="block">
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img loading="lazy" decoding="async"
                          src={post.thumbnail}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="px-2.5 py-1 text-xs font-medium bg-[#1C1917]/80 text-white rounded-full backdrop-blur-sm">
                            {post.categoryLabel}
                          </span>
                        </div>
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-1.5 flex-wrap mb-2">
                          {post.tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="text-[10px] text-[#8C8780] bg-[#FAFAF8] px-2 py-0.5 rounded-full border border-[#E8E5E0]">
                              #{tag}
                            </span>
                          ))}
                        </div>
                        <h3 className="text-sm font-bold text-[#1C1917] mb-2 line-clamp-2 group-hover:text-[#C4956A] transition-colors leading-snug">
                          {post.title}
                        </h3>
                        <p className="text-xs text-[#8C8780] line-clamp-2 mb-3 leading-relaxed">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-[#B8B3AE]">
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full overflow-hidden border border-[#E8E5E0]">
                              <img loading="lazy" decoding="async" src={post.authorAvatar} alt={post.author} className="w-full h-full object-cover" />
                            </div>
                            <span>{post.author}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span>{post.createdAt}</span>
                            <span>·</span>
                            <span>{post.readTime}분</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-20"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#C4956A]/10 flex items-center justify-center">
                  <i className="ri-search-line text-2xl text-[#C4956A]"></i>
                </div>
                <p className="text-sm text-[#8C8780] mb-1">검색 결과가 없습니다</p>
                <p className="text-xs text-[#B8B3AE]">다른 키워드나 카테고리로 검색해보세요</p>
                <button
                  onClick={() => { setActiveCategory("all"); setSearchQuery(""); }}
                  className="mt-4 px-5 py-2 text-xs font-medium text-[#C4956A] border border-[#C4956A]/30 rounded-full hover:bg-[#C4956A]/5 transition-all"
                >
                  전체 글 보기
                </button>
              </motion.div>
            )}
          </motion.section>
        </div>
      </main>
      <Footer />
    </div>
  );
}